'use client';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import debounce from 'lodash/debounce';
import axios from 'axios';
import axiosInstance from '../../../../services/AxiosInstance';
import { Formik, Form as FormikForm, Field } from 'formik';
import * as Yup from 'yup';
import Tables from '../../common/Tables/Tables.jsx';
import TextInput from '../../common/FormFields/TextInput.jsx';
import { getUnifiedStudentProfile } from '../../../../services/StudentProfileService';
import { getAttendanceRecords } from '../../../../services/StudentDashboardService';
import { getMe } from '../../../../services/AuthService';
import { createOrUpdateExtendedProfile } from '../../../../services/StudentService';
import { useParams } from 'react-router-dom';
import { toast } from '../../common/Toast/toast';
import { Skeleton, SkeletonGroup } from '../../common/Skeleton/Skeleton.jsx';
import { silenceFor } from '../../common/Loader/busy';
import { FaUserTie } from 'react-icons/fa';
import {
  Edit,
  Save,
  X,
  User,
  CreditCard,
  CheckCircle,
  Award,
  BookOpen,
  TrendingUp,
  Users,
  Eye,
  FileText,
  Download,
  File,
  Layers,
  // Upload,
  Clock,
  Calendar,
  Mail,
  Phone,
  MapPin,
} from 'lucide-react';
import CustomAsyncDropdown from '../../common/NewCustomAsyncSelectDropdown.jsx';
import { isStaffViewer } from '../Dashboard/StudentDashboard2.jsx';
import './StudentProfile.css';

/* =============================================================
   SHARED READERS

   Small enough to sit outside the component, so they are not rebuilt
   on every render of a page this size.
   ============================================================= */
const initialsOf = (text, fallback = '?') => {
  const parts = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  return parts.slice(0, 2).map((w) => w[0].toUpperCase()).join('');
};

const fmtDay = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/* The staff view's speedometer, the same half circle of radius 80 the
   other two dashboards draw, so the three cannot drift apart. */
const GAUGE_R = 80;
const GAUGE_ARC = `M 20 100 A ${GAUGE_R} ${GAUGE_R} 0 0 1 180 100`;
const GAUGE_LEN = Math.PI * GAUGE_R;

/* The student view's ring is a whole circle of radius 52, so its length
   is a full circumference — 2πr, not πr. */
const RING_LEN = 2 * Math.PI * 52;

const StudentProfile = () => {
  const [activeTab, setActiveTab] = useState('Account Details');
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [attendanceStats, setAttendanceStats] = useState(null);
  const [batchWiseAttendance, setBatchWiseAttendance] = useState([]);
  const [batchesData, setBatchesData] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  // console.log(setBatchLoading); // To avoid unused var correctly correctly correctly correctamente.
  const [attendanceData, setAttendanceData] = useState({});
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendancePagination, setAttendancePagination] = useState({}); // { batchId: { page: 1, hasNextPage: false } }
  const [loadingBatchId, setLoadingBatchId] = useState(null); // track specifically which batch is loading more
  const [motivationalQuote, setMotivationalQuote] = useState('');
  const [receiptsData, setReceiptsData] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  // console.log(setReceiptsLoading); // To avoid unused var correctly correctamente.
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  // console.log("recepit data",receiptsData)
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  // const [resumeFile, setResumeFile] = useState(null);
  // const [resumeFileUrl, setResumeFileUrl] = useState(null);
  // const [showResumeModal, setShowResumeModal] = useState(false);
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [incompleteFields, setIncompleteFields] = useState([]);
  const [studentCourses, setStudentCourses] = useState([]);
  const [dynamicAmounts, setDynamicAmounts] = useState({
    totalAmount: 0,
    paidAmount: 0,
    remainingAmount: 0,
  });
  // Download states
  const [downloadingReceipt, setDownloadingReceipt] = useState(null);

  // Admin-only states
  // const [currentUserData, setCurrentUserData] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  // const [allStudents, setAllStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [selectedStudentName, setSelectedStudentName] = useState('');
  // const [selectedStudentEmail, setSelectedStudentEmail] = useState('');
  const [selectedStudentValue, setSelectedStudentValue] = useState(null);
  
  // Student List States for Selection
  const [studentOptions, setStudentOptions] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [hasMoreStudents, setHasMoreStudents] = useState(true);
  const [studentPage, setStudentPage] = useState(1);
  const [studentSearchTerm, setStudentSearchTerm] = useState("");
  const isFetchingStudentsRef = useRef(false);

  const [selectedSyllabus, setSelectedSyllabus] = useState(null);
  const [showSyllabusModal, setShowSyllabusModal] = useState(false);

  const { id } = useParams();
  const isFetchingRef = useRef(false);
  const initialFetchRef = useRef(false);

  /* Who is looking. The rule lives in StudentDashboard2 so the profile
     and the dashboard cannot disagree about who counts as staff: any
     named profile that is not a student, plus superadmins. It used to
     test for 'admin' alone, which sent a trainer down the student path
     and tried to load a trainer's own student record.

     The state is still called isAdmin because the fetch logic below
     reads it in a dozen places; isStaff is the name the render uses. */
  const checkIfAdmin = (userData) => isStaffViewer(userData);

  const motivationalQuotes = [
    'Success is the sum of small efforts repeated day in and day out.',
    'The expert in anything was once a beginner.',
    "Don't watch the clock; do what it does. Keep going.",
    'The secret of getting ahead is getting started.',
    "You don't have to be great to start, but you have to start to be great.",
    "Believe you can and you're halfway there.",
    'Your education is a dress rehearsal for a life that is yours to lead.',
    'Learning is a treasure that will follow its owner everywhere.',
    'The capacity to learn is a gift; the ability to learn is a skill; the willingness to learn is a choice.',
    'Education is the passport to the future, for tomorrow belongs to those who prepare for it today.',
  ];

  // Helper function to get course names from courseModuleIds using CRM courses
  /* const getCourseNamesFromModuleIds = useCallback((courseModuleIds, batches) => {
    if (!courseModuleIds || !batches) {
      return [];
    }

    // Split comma-separated IDs
    const moduleIds = courseModuleIds.split(',').map((id) => id.trim());
    const courseNames = [];

    moduleIds.forEach((moduleId) => {
      // Find matching course from batches
      const batchWithCourse = batches.find(b => {
        const cId = b.courseDetails?.id || b.courseId;
        if (Array.isArray(cId)) {
          return cId.some(c => (typeof c === 'object' ? c.id === moduleId : c === moduleId));
        }
        return cId === moduleId;
      });

      if (batchWithCourse && batchWithCourse.courseDetails?.name) {
        courseNames.push(batchWithCourse.courseDetails.name.trim());
      }
    });

    return [...new Set(courseNames)];
  }, []); */

  const validationSchema = useMemo(() => Yup.object({
    studentName: Yup.string().required('Name is required'),
    studentEmail: Yup.string().email('Invalid email').required('Email is required'),
    phone: Yup.string().required('Phone number is required'),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    documentNo: Yup.string().required('Document Number is required'),
    collegeName: Yup.string().required('College Name is required'),
    courseName: Yup.string().required('Course Name is required'),
    permanentAddress: Yup.string().required('Permanent Address is required'),
    currentAddress: Yup.string().required('Current Address is required'),
    tenthMarks: Yup.string().required('10th Marks is required'),
    twelfthMarks: Yup.string().required('12th Marks is required'), // Optional
  }), []);

  // const headers = ['Date', 'Amount Paid', 'Payment Method', 'Actions'];
  const batchHeaders = ['Batch Name', 'Course', 'Instructor', 'Start Date', 'End Date'];

  const getRandomQuote = useCallback(() => {
    const now = new Date();
    const storedQuote = localStorage.getItem('motivationalQuote');
    const storedDate = localStorage.getItem('motivationalQuoteDate');
    if (storedQuote && storedDate) {
      const quoteDate = new Date(storedDate);
      const diffDays = Math.floor((now - quoteDate) / (1000 * 60 * 60 * 24));
      if (diffDays < 3) {
        return storedQuote;
      }
    }
    const randomIndex = Math.floor(Math.random() * motivationalQuotes.length);
    const newQuote = motivationalQuotes[randomIndex];
    localStorage.setItem('motivationalQuote', newQuote);
    localStorage.setItem('motivationalQuoteDate', now.toString());
    return newQuote;
  }, [motivationalQuotes]);

  /* const getPaymentStatus = (receipt) => {
    const remainingAmount = receipt?.remainingAmount;
    if (remainingAmount === 0 || remainingAmount === '0') return 'Paid';
    return 'Pending';
  }; */

  const getAttendanceStatus = (percentage) => {
    if (percentage >= 80) return { text: '✓ Excellent', color: '#56ab2f' };
    if (percentage >= 60) return { text: '⚠ Good', color: '#fdbb2d' };
    return { text: '⚠ Needs improvement', color: '#ff7e5f' };
  };

  const formatBatchTiming = (batch) => {
    if (!batch) return null;

    const startTime = batch.startTime
      ? new Date(batch.startTime).toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      })
      : 'N/A';

    const endTime = batch.endTime
      ? new Date(batch.endTime).toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      })
      : 'N/A';

    const startDate = batch.startTime
      ? new Date(batch.startTime).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
      : 'N/A';

    return { startTime, endTime, startDate };
  };

  // Function to handle PDF download - integrated from billing-ui ListReceipts.jsx
  const handleDownloadReceipt = async (receiptData) => {
    if (!receiptData) {
      toast.error('No receipt data available to download');
      return { success: false };
    }

    const receiptId = receiptData.id || receiptData._id;
    setDownloadingReceipt(receiptId);

    try {
      // Get branch address from receipt data (now joined in profile response)
      const branchAddress =
        receiptData.preferredBranchId?.[0]?.address ||
        '41/A & 42 A-Wing, Shreenath Plaza, 1st floor, Dnyaneshwar Paduka Chowk, 1184/4 F.C Road, Shivaji Nagar, Pune, Maharashtra – 411005 India';

      // Create PDF payload matching backend expectations
      const pdfPayload = {
        receipt_number: receiptData.receiptNo || '',
        receipt_date: receiptData.receiptDate || receiptData.date || '',
        student_name: receiptData.studentName || '',
        mobile: receiptData.mobile || receiptData.studentNumber || receiptData.phone || '',
        email: receiptData.studentEmail || receiptData.email || '',
        course: receiptData.courseName || receiptData.courseIds?.[0]?.name || receiptData.course || '',
        course_title: receiptData.courseName || receiptData.courseIds?.[0]?.name || receiptData.course || '',
        total_amount: receiptData.totalAmount || '',
        paid_amount: receiptData.paidAmount || '',
        paid_receipt_amount: receiptData.paidReceiptAmount || receiptData.amountPaid || '',
        remaining_amount: receiptData.remainingAmount || '',
        fees_due_date: receiptData.feesDueDate || '',
        payment_mode: receiptData.paymentMode || '',
        contact_person: receiptData.counsellorName || receiptData.contactPersonId?.[0]?.name || receiptData.contactPersonName || '',
        contact_no: receiptData.counsellorNumber || receiptData.contactPersonId?.[0]?.phone || receiptData.contactPersonPhone || '',
        receipt_type: receiptData.ReceiptType || receiptData.receiptType || '',
        fine_amount: receiptData.fineAmount || '0',
        studentIdNumber: receiptData.studentIdNumber || '',
        branchAddress: branchAddress,
      };

      // Request PDF from backend directly using axiosInstance
      const response = await axiosInstance.post(`/mailer/generate-receipt-pdf`, pdfPayload, {
        responseType: 'blob',
        validateStatus: (status) => true
      });

      if (response.status === 200) {
        // Get PDF blob from response
        const pdfBlob = response.data;

        // Create download link
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Receipt_${receiptData.receiptNo || 'Download'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('PDF downloaded successfully!', { toast: true });
        return { success: true };
      } else {
        const result = await (async () => {
          try {
            const text = await response.data.text();
            return JSON.parse(text);
          } catch (e) {
            return { message: 'API error' };
          }
        })();
        const errorMessage = typeof result.message === 'object' ? result.message.message : result.message;
        toast.error(`Failed to generate PDF: ${errorMessage || 'Unknown error'}`);
        return { success: false, message: errorMessage };
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Error downloading PDF. Please try again.');
      return { success: false };
    } finally {
      setDownloadingReceipt(null);
    }
  };

  // Fetch courses from batches like StudentDashboard2

  /* const handleResumeUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setResumeFile(file);
      const fileUrl = URL.createObjectURL(file);
      setResumeFileUrl(fileUrl);
      Swal.fire({
        title: 'Success!',
        text: 'Resume uploaded successfully',
        icon: 'success',
        confirmButtonText: 'OK',
      });
    } else {
      setResumeFile(null);
      setResumeFileUrl(null);
      Swal.fire({
        title: 'Error!',
        text: 'Please upload a PDF file only.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  }; */

  // For admin: handler for student selection - use receipt object id and call /receipts/id
  const handleUnifiedResponse = useCallback(async (unifiedData) => {
    const student = unifiedData.student;
    const academic = unifiedData.academincInfo || {};
    const viewer = unifiedData.viewer;

    if (viewer) {
      const adminStatus = checkIfAdmin(viewer);
      setIsAdmin(adminStatus);
      // setCurrentUserData(viewer);
      
      /* if (unifiedData.studentsList) {
        setAllStudents(unifiedData.studentsList);
      } */
    }

    if (!student) {
      if (!unifiedData.studentsList) {
        setProfileData(null);
      }
      return;
    }

    // Map Batches - Utilize details from backend if available
    const batches = Array.isArray(unifiedData.batches) ? unifiedData.batches.map(v => {
      // Handle the new array-based courseId and trainerId structure
      const courseObj = Array.isArray(v.courseId) && v.courseId.length > 0 ? v.courseId[0] : null;
      const trainerObj = Array.isArray(v.trainerId) && v.trainerId.length > 0 ? v.trainerId[0] : null;

      return {
        ...v,
        courseDetails: v.courseDetails || courseObj,
        trainerDetails: v.trainerDetails || trainerObj,
      };
    }) : [];

    setBatchesData(batches);

    /* const uniqueCourses = [...new Set(batches.map(b => b.courseDetails?.name || b.name || 'Course'))];
    setStudentCourses(uniqueCourses); */

    // Map data to match existing state structure
    const firstBatchCourse = batches.length > 0 ? batches[0].courseDetails?.name : '';
    const officialCourse = Array.isArray(unifiedData.courses) && unifiedData.courses.length > 0 ? unifiedData.courses[0].course?.name : '';
    const completeProfileData = {
      ...student,
      studentId: student.id,
      studentName: student.name,
      studentEmail: student.email,
      phone: student.phone,
      mobile: student.phone,
      studentIdNumber: (unifiedData.receipts || []).find(r => r.studentIdNumber)?.studentIdNumber || student.studentIdNumber || '',
      studentNumber: (unifiedData.receipts || []).find(r => r.studentIdNumber)?.studentIdNumber || student.studentIdNumber || '',
      gender: student.gender,
      city: student.city,
      state: student.state,
      documentNo: student.documentNo,
      documentType: student.documentType,
      collegeName: student.collegeName,
      profession: student.profession,
      courseName: officialCourse || student.courseName || firstBatchCourse || '',
      courseModuleIds: student.courseIds,
      // Academic info
      tenthMarks: academic.tenthMarks || '',
      twelfthMarks: academic.twelfthMarks || '',
      permanentAddress: academic.permanentAddress || '',
      currentAddress: academic.address || '',
      aadharNo: academic.aadharNo || '',
    };

    setProfileData(completeProfileData);

    // Process Attendance from History
    const history = unifiedData.userAttendance || unifiedData.attendanceHistory || [];
    if (history.length > 0) {
      const attendanceByBatch = {};
      history.forEach((record) => {
        if (!attendanceByBatch[record.batchId]) {
          attendanceByBatch[record.batchId] = [];
        }
        attendanceByBatch[record.batchId].push(record);
      });
      setAttendanceData(attendanceByBatch);
    }

    // Map Receipts
    const mappedReceipts = (unifiedData.receipts || []).map((r) => ({
      ...r,
      studentName: student.name,
      studentEmail: student.email,
      phone: student.phone,
      mobile: student.phone,
      studentIdNumber: r.studentIdNumber || student.studentIdNumber || '',
      studentNumber: r.studentIdNumber || student.studentIdNumber || '',
      date: r.receiptDate ? new Date(r.receiptDate).toLocaleDateString('en-GB') : 'NA',
      amountPaid:
        r.ReceiptType === 'New Enrollment' || r.ReceiptType === 'Second New Enrollment'
          ? r.paidAmount
          : r.paidReceiptAmount,
      status:
        r.remainingAmount === 0 || r.remainingAmount === '0' || r.remainingAmount === null
          ? 'Paid'
          : r.ReceiptType || 'Unknown',
    }));
    setReceiptsData(mappedReceipts);

    setAttendanceStats({
      attendancePercentage: unifiedData.attendancePercentage || 0,
      presentCount: unifiedData.classesAttended || unifiedData.totalAttended || 0,
      totalLectures: unifiedData.totalClasses || 0,
    });

    if (unifiedData.batchWiseAttendance) {
      setBatchWiseAttendance(unifiedData.batchWiseAttendance);
    }

    setStudentCourses(unifiedData.courses || []);

    setDynamicAmounts({
      totalAmount: student.totalFees || (unifiedData.receipts || []).reduce((acc, curr) => acc + (curr.totalAmount || 0), 0) || 0,
      paidAmount: student.paidAmount || (unifiedData.receipts || []).reduce((acc, curr) => acc + (curr.paidAmount || 0), 0) || 0,
      remainingAmount: student.remainingFees || 0,
    });
  }, []);
  
  // Fetch lists of students for the admin dropdown
  const fetchStudents = useCallback(async (search = "", page = 1) => {
    try {
      isFetchingStudentsRef.current = true;
      const res = await getUnifiedStudentProfile({
        includeStudentsList: true,
        studentSearch: search,
        studentPage: page,
        studentLimit: 10,
      });

      const payload = res?.data || {};
      const list = Array.isArray(payload.studentsList) ? payload.studentsList : [];
      const hasNext = Boolean(payload.studentsListPagination?.hasNext);

      return { students: list, hasMore: hasNext };
    } catch (e) {
      console.error("Error fetching students list:", e);
      return { students: [], hasMore: false };
    } finally {
      isFetchingStudentsRef.current = false;
    }
  }, []);

  const loadStudents = useCallback(
    async (search = "", pageNum = 1, append = false) => {
      setLoadingStudents(true);
      const { students: studentsList, hasMore } = await fetchStudents(
        search,
        pageNum
      );

      const newOptions = studentsList.map((s) => ({
        value: s.id,
        label: `${s.name} | ${s.email}`,
        student: s,
      }));

      if (append) {
        setStudentOptions((prev) => [...prev, ...newOptions]);
      } else {
        setStudentOptions(newOptions);
      }

      setHasMoreStudents(hasMore);
      setLoadingStudents(false);
    },
    [fetchStudents]
  );

  const handleLoadMore = useCallback(async () => {
    if (!hasMoreStudents || loadingStudents || isFetchingStudentsRef.current)
      return;
    const nextPage = studentPage + 1;
    setStudentPage(nextPage);
    await loadStudents(studentSearchTerm, nextPage, true);
  }, [
    hasMoreStudents,
    loadingStudents,
    studentPage,
    studentSearchTerm,
    loadStudents,
  ]);

  const handleStudentSearch = useMemo(
    () =>
      debounce(async (inputValue) => {
        setStudentSearchTerm(inputValue);
        setStudentPage(1);
        await loadStudents(inputValue, 1, false);
      }, 700),
    [loadStudents]
  );

  const handleDropdownOpen = useCallback(async () => {
    if (isAdmin && studentOptions.length === 0 && !loadingStudents) {
      await loadStudents("", 1, false);
    }
  }, [isAdmin, studentOptions.length, loadingStudents, loadStudents]);

  const handleRefreshStudents = useCallback(async () => {
    setStudentSearchTerm("");
    setStudentPage(1);
    await loadStudents("", 1, false);
  }, [loadStudents]);

  const fetchUnifiedProfile = useCallback(async (fetchParams = {}, skipStudentList = false, asAdmin = false) => {
    if (isFetchingRef.current) return;
    const studentId = fetchParams.studentId || null;
    const email = fetchParams.email || null;
    
    try {
      isFetchingRef.current = true;
      // The page shows its own skeleton, so the global overlay stays down.
      silenceFor();
      setLoading(true);
      setError(null);
      
      const params = {};
      if (studentId) params.studentId = studentId;
      if (email) params.email = email;
      
      // Only include students list if we're an admin and haven't selected a student or explicitly asked for it
      if (asAdmin && !skipStudentList) {
        params.includeStudentsList = true;
      }

      const response = await getUnifiedStudentProfile(params);
      
      if (!response || response.isError) {
        setError(response?.message?.message || 'Failed to fetch student profile');
        setProfileData(null);
        setLoading(false);
        return;
      }

      if (response.data?.student) {
        const student = response.data.student;
        setSelectedStudentId(student.id);
        setSelectedStudentName(student.name || '');
        // setSelectedStudentEmail(student.email || '');
        setSelectedStudentValue({
          value: student.id,
          label: `${student.name} | ${student.email}`,
          student: student
        });
      }

      await handleUnifiedResponse(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error in fetchUnifiedProfile:', err);
      setError('Failed to fetch student profile');
      setLoading(false);
    } finally {
      isFetchingRef.current = false;
    }
  }, [handleUnifiedResponse]);

  const handleStudentSelection = useCallback(async (selectedValues) => {
    const selectedOption = Array.isArray(selectedValues) ? selectedValues[0] : selectedValues;
    if (!selectedOption) {
        handleClearSelection();
        return;
    }
    const studentId = selectedOption?.value ? String(selectedOption.value) : '';
    const student = selectedOption?.student;

    setSelectedStudentId(studentId);
    setSelectedStudentName(student?.name || '');
    // setSelectedStudentEmail(student?.email || '');
    setSelectedStudentValue(selectedOption);

    if (student?.email) {
      await fetchUnifiedProfile({ email: student.email }, true, isAdmin);
    } else {
      await fetchUnifiedProfile({ studentId }, true, isAdmin);
    }
  }, [fetchUnifiedProfile, isAdmin]);

  const handleClearSelection = () => {
    setSelectedStudentId(null);
    setSelectedStudentName('');
    // setSelectedStudentEmail('');
    setSelectedStudentValue(null);
    setProfileData(null);
  };

  const fetchStudentDataById = async (studentId) => {
    await fetchUnifiedProfile(studentId, false, isAdmin);
  };

  const fetchStudentDataForCurrentStudent = async () => {
    await fetchUnifiedProfile(null, true, isAdmin);
  };


  /* const renderCell = (row, header) => {
    const columnRenderers = {
      Date: row.date,
      'Amount Paid': row.amountPaid,
      'Payment Method': row.paymentMode,
      Actions: (
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => handleViewReceipt(row)}
          className="d-flex align-items-center"
        >
          <Eye size={14} className="me-1" />
          View Receipt
        </Button>
      ),
    };
    return columnRenderers[header] || row[header];
  }; */

  const renderBatchCell = (row, header) => {
    // Helper function to format dates as dd/mm/yy
    const formatDateToDDMMYY = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A'; // Invalid date check
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    };

    const timing = formatBatchTiming(row);

    const columnRenderers = {
      'Batch Name': row.name || row.batchName || 'N/A',
      Course: row.courseDetails?.name || row.courseId?.[0]?.name || row.course || 'N/A',
      Instructor:
        row.trainerDetails?.name ||
        row.trainerDetails?.trainerName ||
        row.trainerId?.name ||
        row.instructor ||
        'N/A',
      'Start Date': formatDateToDDMMYY(row.startTime || row.startDate),
      'End Date': formatDateToDDMMYY(row.endTime || row.endDate),
      'Class Schedule': timing ? (
        <div>
          <div>
            {timing.startTime} - {timing.endTime}
          </div>
          <small>{timing.startDate}</small>
        </div>
      ) : (
        'N/A'
      ),
    };
    return columnRenderers[header] || row[header];
  };



  const handleViewReceipt = async (receiptData) => {
    try {
      setSelectedReceipt(receiptData);
      setShowReceiptModal(true);
    } catch (err) {
      setSelectedReceipt(receiptData);
      setShowReceiptModal(true);
    }
  };

  const handleSubmit = async (values) => {
    try {
      // Get studentId from profileData
      const studentId = profileData.studentId;

      if (!studentId) {
        throw new Error('Student ID not found');
      }

      // Prepare data for extended profile API
      const extendedData = {
        userId: studentId, // This matches with studentId in receipt
        studentName: values.studentName || '',
        aadharNo: values.documentNo || '',
        address: 'NA',
        emergencyAddress: 'NA',
        permanentAddress: values.permanentAddress || '',
        currentAddress: values.currentAddress || '',
        tenthMarks: values.tenthMarks || '',
        twelfthMarks: values.twelfthMarks || '',
      };

      // console.log("Submitting extended profile data for studentId:", studentId)
      // console.log("Extended data:", extendedData)

      // **This function now automatically checks if profile exists and updates/creates accordingly**
      const result = await createOrUpdateExtendedProfile(studentId, extendedData);

      if (result.success) {
        // console.log("Profile operation successful:", result.message)

        // Update local state
        setProfileData((prev) => ({
          ...prev,
          permanentAddress: values.permanentAddress || '',
          currentAddress: values.currentAddress || '',
          tenthMarks: values.tenthMarks || '',
          twelfthMarks: values.twelfthMarks || '',
        }));

        setEditMode(false);

        toast.success('Profile updated successfully');

        // Refresh profile data
        if (isAdmin && studentId) {
          await fetchStudentDataById(studentId);
        } else {
          await fetchStudentDataForCurrentStudent();
        }
      } else {
        throw new Error('Update failed');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      toast.error(err.message || 'Failed to update profile. Please try again.');
    }
  };

  useEffect(() => {
    setMotivationalQuote(getRandomQuote());
  }, [getRandomQuote]);

  // Fetch Attendance Records specifically for EACH batch when tab is selected
  useEffect(() => {
    if (activeTab === 'Attendance' && profileData && batchesData.length > 0) {
      // Check if we already have some attendance data – we might not need to fetch every time
      // or at least avoid duplicated calls if already loading
      if (attendanceLoading) return;

      const fetchAllBatchesAttendance = async () => {
        try {
          silenceFor();
          setAttendanceLoading(true);
          const email = profileData.studentEmail || profileData.email;
          const groupedData = { ...attendanceData };
          const paginationData = { ...attendancePagination };
          
          await Promise.all(batchesData.map(async (batch) => {
            const bId = batch._id || batch.id;
            if (!bId) return;

            const params = {
              email: email,
              batchId: bId,
              limit: 10, // Requirement: Load 10 records
              page: 1
            };
            
            if (selectedStudentId) {
              params.studentId = selectedStudentId;
            }

            const res = await getAttendanceRecords(params);
            if (res && !res.isError) {
              groupedData[bId] = res.data || [];
              paginationData[bId] = {
                page: 1,
                hasNextPage: res.pagination?.hasNextPage || (res.data && res.data.length === 10)
              };
            }
          }));
          
          setAttendanceData(groupedData);
          setAttendancePagination(paginationData);
        } catch (error) {
          console.error("Error fetching multi-batch attendance initial data:", error);
        } finally {
          setAttendanceLoading(false);
        }
      };
      
      fetchAllBatchesAttendance();
    }
  }, [activeTab, profileData, batchesData, selectedStudentId]);

  const loadMoreAttendance = async (batchId) => {
    if (!batchId || !profileData || loadingBatchId) return;

    try {
      setLoadingBatchId(batchId);
      const currentPagination = attendancePagination[batchId] || { page: 1, hasNextPage: false };
      const nextPage = currentPagination.page + 1;
      const email = profileData.studentEmail || profileData.email;

      const params = {
        email: email,
        batchId: batchId,
        limit: 10,
        page: nextPage
      };

      if (selectedStudentId) {
        params.studentId = selectedStudentId;
      }

      const res = await getAttendanceRecords(params);
      if (res && !res.isError) {
        const newData = res.data || [];
        setAttendanceData(prev => ({
          ...prev,
          [batchId]: [...(prev[batchId] || []), ...newData]
        }));

        setAttendancePagination(prev => ({
          ...prev,
          [batchId]: {
            page: nextPage,
            hasNextPage: res.pagination?.hasNextPage || (newData.length === 10)
          }
        }));
      }
    } catch (error) {
      console.error("Error loading more attendance:", error);
      toast.error("Failed to load more records");
    } finally {
      setLoadingBatchId(null);
    }
  };

  useEffect(() => {
    if (!profileData) return;
    const checkCompleteness = () => {
      try {
        validationSchema.validateSync(profileData, { abortEarly: false });
        setShowIncompleteModal(false);
        setIncompleteFields([]);
      } catch (err) {
        if (err && err.inner) {
          setIncompleteFields(err.inner.map((e) => e.message));
        } else if (err && err.message) {
          setIncompleteFields([err.message]);
        } else {
          setIncompleteFields(['Profile is incomplete.']);
        }
        setShowIncompleteModal(true);
      }
    };
    checkCompleteness();
    const interval = setInterval(checkCompleteness, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [profileData, validationSchema]);

  // Removed loadStudentOptions as we now use loadStudents logic

  useEffect(() => {
    if (initialFetchRef.current) return;
    
    const fetchInitialData = async () => {
      try {
        if (isFetchingRef.current) return;
        initialFetchRef.current = true;
        
        silenceFor();
        setLoading(true);
        const currentUser = await getMe();
        const userData = currentUser.data;
        // setCurrentUserData(userData);

        const adminStatus = checkIfAdmin(userData);
        setIsAdmin(adminStatus);

        if (id) {
          // If ID in URL, fetch it directly (works for both admin and student)
          await fetchUnifiedProfile({ studentId: id }, !adminStatus, adminStatus);
        } else if (!adminStatus) {
          // If student and no ID, fetch current student profile
          await fetchUnifiedProfile({}, true, false);
        } else {
          // If admin and no ID, we wait for selection
          // Dropdown will fetch student list when mounted or opened
          setLoading(false);
        }
      } catch (err) {
        setError('Failed to initialize profile view');
        setLoading(false);
        initialFetchRef.current = false; // Allow retry on error
      }
    };

    fetchInitialData();
  }, [id, fetchUnifiedProfile]);

  /* The name the render uses. isAdmin is the fetch layer's name for the
     same flag; see checkIfAdmin above. */
  const isStaff = isAdmin;

  /* One dropdown, rendered into the gate before a student is picked and
     into the picker strip afterwards. */
  const studentPicker = (
    <CustomAsyncDropdown
      inputId="sp-student-picker"
      options={studentOptions}
      value={selectedStudentValue ? [selectedStudentValue] : []}
      onChange={handleStudentSelection}
      placeholder="Search by name, email or phone..."
      isMulti={false}
      isSearchable={true}
      isLoading={loadingStudents}
      onInputChange={handleStudentSearch}
      onMenuScrollToBottom={handleLoadMore}
      onMenuOpen={handleDropdownOpen}
      onRefresh={handleRefreshStudents}
      isDisabled={false}
      loadingMessage="Loading students..."
      noOptionsMessage="No students found"
      noSearchResultsMessage="No students matched your search"
    />
  );

  /* =============================================================
     EARLY STATES

     All four are drawn in the staff frame — a viewer who has not got
     a profile yet has no identity to hang the LinkedIn card on, and
     the dashed panel reads the same either way.
     ============================================================= */
  if (loading) {
    /* The profile's own shape, shimmering: who it is, the tab row, and two
       cards of fields -- in the profile's .pf-card frame, so the page does
       not jump when the record arrives. */
    return (
      <div className="dashboard-wrapper">
        <div className="td-page">
          <SkeletonGroup label="Loading profile" className="lms-skel-stack">
            <section className="pf-card">
              <div className="pf-card-body">
                <div className="lms-skel-row">
                  <Skeleton shape="mark" />
                  <div className="lms-skel-stack lms-skel-grow">
                    <Skeleton shape="title" width="40" />
                    <Skeleton width="25" />
                  </div>
                </div>
              </div>
            </section>

            <div className="lms-skel-row">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton shape="tab" key={i} />
              ))}
            </div>

            {[0, 1].map((card) => (
              <section className="pf-card" key={card}>
                <div className="pf-card-head">
                  <Skeleton shape="title" width="25" />
                </div>
                <div className="pf-card-body">
                  <div className="pf-fields">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <div className="lms-skel-stack" key={i}>
                        <Skeleton width="40" />
                        <Skeleton width="70" />
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            ))}
          </SkeletonGroup>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-wrapper">
        <div className="td-page">
          <div className="sp-gate sp-gate--bad">
            <span className="sp-gate-mark">
              <X size={22} aria-hidden="true" />
            </span>
            <h1 className="sp-gate-title">Could not load the profile</h1>
            <p className="sp-gate-text">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  /* Staff arrive without a subject. Nothing to draw but the picker. */
  if (isStaff && !selectedStudentId && !id) {
    return (
      <div className="dashboard-wrapper">
        <div className="td-page">
          <div className="sp-gate">
            <span className="sp-gate-mark">
              <User size={22} aria-hidden="true" />
            </span>
            <h1 className="sp-gate-title">Pick a student</h1>
            <p className="sp-gate-text">
              Choose a student to read their profile, batches, attendance and fees.
            </p>
            <div className="sp-gate-field">{studentPicker}</div>
            <span className="sp-gate-tip">
              <FileText size={14} aria-hidden="true" />
              Search by name, email or phone number.
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="dashboard-wrapper">
        <div className="td-page">
          <div className="sp-gate">
            <span className="sp-gate-mark">
              <User size={22} aria-hidden="true" />
            </span>
            <h1 className="sp-gate-title">No profile found</h1>
            <p className="sp-gate-text">There is no profile record for this student.</p>
          </div>
        </div>
      </div>
    );
  }

  /* =============================================================
     DERIVED — read once, rendered by whichever view is showing
     ============================================================= */
  const attendancePercentage = attendanceStats?.attendancePercentage || 0;
  const attendanceStatus = getAttendanceStatus(attendancePercentage);

  const displayName = profileData.studentName || profileData.name || 'Student';
  const displayEmail = profileData.studentEmail || profileData.email || '';

  const dash = (value) => {
    const text = value === 0 ? '0' : String(value ?? '').trim();
    return text || null;
  };

  /* The record, as label/value pairs. `wide` marks a value too long to
     sit in one column of the grid. */
  const PERSONAL = [
    { label: 'Full name', value: displayName },
    { label: 'Email', value: displayEmail },
    { label: 'Phone', value: profileData.phone },
    { label: 'Gender', value: profileData.gender },
    { label: 'City', value: profileData.city },
    { label: 'State', value: profileData.state },
  ];

  const ACADEMIC = [
    { label: 'Course', value: profileData.courseName },
    { label: 'College', value: profileData.collegeName },
    { label: 'Profession', value: profileData.profession },
    { label: 'Document type', value: profileData.documentType },
    { label: 'Document number', value: profileData.documentNo },
    { label: '10th marks', value: profileData.tenthMarks },
    { label: '12th marks', value: profileData.twelfthMarks },
  ];

  /* Per-batch attendance, joined to the batch it belongs to so the
     summary and the day list read off one row. */
  const attendanceByBatch = batchesData.map((batch) => {
    const batchId = batch._id || batch.id;
    const summary = batchWiseAttendance.find((s) => s.batchId === batchId);
    const records = attendanceData[batchId] || [];
    const total = summary ? summary.totalClasses : records.length;
    const present = summary
      ? summary.attendedClasses
      : records.filter((r) => r.attendance === 'present' || r.studentStatus === 'present').length;
    const pct = total > 0 ? Math.round((present / total) * 100) : 0;
    return { batch, batchId, records, total, present, pct };
  });

  const attTone = (pct) => {
    if (pct >= 80) return 'is-high';
    if (pct < 60) return 'is-low';
    return '';
  };

  const staffAttTone = (pct) => {
    if (pct >= 85) return 'is-high';
    if (pct < 50) return 'is-low';
    return 'is-mid';
  };

  const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

  const TABS = [
    { key: 'Account Details', label: 'Personal', icon: User },
    { key: 'Batch Information', label: 'Batches', icon: Layers, count: batchesData.length },
    { key: 'Attendance', label: 'Attendance', icon: CheckCircle },
    { key: 'Billing Information', label: 'Fees', icon: CreditCard, count: receiptsData.length },
  ];

  /* =============================================================
     THE DIALOGS — one pair, opened by either view
     ============================================================= */
  const receiptModal = showReceiptModal ? (
    <div
      className="pm"
      role="dialog"
      aria-modal="true"
      aria-label="Receipt details"
      onClick={() => setShowReceiptModal(false)}
    >
      <div className="pm-panel" onClick={(e) => e.stopPropagation()}>
        <div className="pm-head">
          <FileText size={20} aria-hidden="true" />
          <h2 className="pm-title">Receipt details</h2>
          <button
            type="button"
            className="pm-close"
            onClick={() => setShowReceiptModal(false)}
            aria-label="Close"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="pm-body">
          {selectedReceipt ? (
            <>
              <div className="pm-banner">
                <p className="pm-banner-title">Payment receipt</p>
                <p className="pm-banner-sub">
                  Receipt #{selectedReceipt.receiptNo || selectedReceipt._id?.slice(-6) || 'N/A'}
                </p>
              </div>

              <p className="pm-sec-title">Payment</p>
              <dl className="pm-fields">
                <div>
                  <dt>Receipt no</dt>
                  <dd>{selectedReceipt.receiptNo || '—'}</dd>
                </div>
                <div>
                  <dt>Receipt date</dt>
                  <dd>
                    {selectedReceipt.receiptDate
                      ? new Date(selectedReceipt.receiptDate).toLocaleDateString('en-GB')
                      : '—'}
                  </dd>
                </div>
                <div>
                  <dt>Due date</dt>
                  <dd>
                    {selectedReceipt.feesDueDate
                      ? new Date(selectedReceipt.feesDueDate).toLocaleDateString('en-GB')
                      : '—'}
                  </dd>
                </div>
                <div>
                  <dt>Course</dt>
                  <dd>{selectedReceipt.courseIds?.[0]?.name || '—'}</dd>
                </div>
                <div>
                  <dt>Total amount</dt>
                  <dd>{money(selectedReceipt.totalAmount)}</dd>
                </div>
                <div>
                  <dt>Paid amount</dt>
                  <dd>
                    {money(
                      selectedReceipt.ReceiptType === 'New Enrollment'
                        ? selectedReceipt.paidAmount
                        : selectedReceipt.paidReceiptAmount,
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Remaining</dt>
                  <dd>{money(selectedReceipt.remainingAmount)}</dd>
                </div>
                <div>
                  <dt>Payment method</dt>
                  <dd>{selectedReceipt.paymentMode || '—'}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>{selectedReceipt.status || '—'}</dd>
                </div>
              </dl>

              <p className="pm-sec-title">Student</p>
              <dl className="pm-fields">
                <div>
                  <dt>Name</dt>
                  <dd>{selectedReceipt.studentName || '—'}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>{selectedReceipt.studentEmail || '—'}</dd>
                </div>
              </dl>

              {selectedReceipt.description && (
                <>
                  <p className="pm-sec-title">Description</p>
                  <p className="pm-note">{selectedReceipt.description}</p>
                </>
              )}
            </>
          ) : (
            <p className="pm-note">No receipt selected.</p>
          )}
        </div>

        <div className="pm-foot">
          <button type="button" className="pm-btn" onClick={() => setShowReceiptModal(false)}>
            Close
          </button>
          <button
            type="button"
            className="pm-btn pm-btn--go"
            onClick={async () => {
              const result = await handleDownloadReceipt(selectedReceipt);
              if (result && result.success) {
                toast.success('Receipt downloaded successfully!', { toast: true });
              } else {
                toast.error(result?.message || 'Failed to download receipt. Please try again.');
              }
            }}
          >
            <Download size={15} aria-hidden="true" />
            Download
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const syllabusModal = showSyllabusModal ? (
    <div
      className="pm"
      role="dialog"
      aria-modal="true"
      aria-label="Syllabus topic"
      onClick={() => setShowSyllabusModal(false)}
    >
      <div className="pm-panel" onClick={(e) => e.stopPropagation()}>
        <div className="pm-head">
          <BookOpen size={20} aria-hidden="true" />
          <h2 className="pm-title">Syllabus topic</h2>
          <button
            type="button"
            className="pm-close"
            onClick={() => setShowSyllabusModal(false)}
            aria-label="Close"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <div className="pm-body">
          <p className="pm-note">{selectedSyllabus}</p>
        </div>
        <div className="pm-foot">
          <button type="button" className="pm-btn" onClick={() => setShowSyllabusModal(false)}>
            Close
          </button>
        </div>
      </div>
    </div>
  ) : null;

  /* =============================================================
     STAFF VIEW

     The admin and trainer kit: .td-page, the dark name box with the
     attendance speedometer, .ov-tiles for the figures, .td-tbl for
     every list.
     ============================================================= */
  if (isStaff) {
    return (
      <div className="dashboard-wrapper">
        <div className="td-page">
          <div className="sp-picker">
            <div className="sp-picker-id">
              <span className="sp-avatar">{initialsOf(displayName)}</span>
              <div>
                <div className="sp-picker-name">{displayName}</div>
                <div className="sp-picker-mail">{displayEmail || 'No email on record'}</div>
              </div>
            </div>

            <div className="sp-picker-field">
              <label className="sp-picker-label" htmlFor="sp-student-picker">Viewing student</label>
              {studentPicker}
            </div>

            <button type="button" className="sp-clear" onClick={handleClearSelection}>
              <X size={13} aria-hidden="true" />
              Clear
            </button>
          </div>

          <div className="sp-top">
            <section className="bp-hero">
              <span className="bp-hero-eyebrow">Student profile</span>
              <h1 className="bp-hero-title">{displayName}</h1>

              <div className="bp-hero-figure">
                <span className="bp-hero-label">Attendance</span>
                <div
                  className="bp-gauge"
                  role="img"
                  aria-label={`Attendance ${attendancePercentage} percent`}
                >
                  <svg viewBox="0 0 200 116" aria-hidden="true">
                    <path className="bp-gauge-track" d={GAUGE_ARC} />
                    <path
                      className="bp-gauge-fill"
                      d={GAUGE_ARC}
                      strokeDasharray={GAUGE_LEN}
                      strokeDashoffset={GAUGE_LEN * (1 - attendancePercentage / 100)}
                    />
                  </svg>
                  <div className="bp-gauge-face">
                    <b>{attendancePercentage}</b>
                    <small>{attendanceStatus.text.replace(/^[^A-Za-z]+/, '')}</small>
                  </div>
                  <div className="bp-gauge-scale" aria-hidden="true">
                    <span>0</span>
                    <span>100</span>
                  </div>
                </div>
                <p className="bp-hero-note">
                  Classes attended against classes held, across every batch.
                </p>
              </div>

              <dl className="bp-hero-facts">
                <div>
                  <dt>Student number</dt>
                  <dd>{profileData.studentNumber || '—'}</dd>
                </div>
                <div>
                  <dt>Batches</dt>
                  <dd>{batchesData.length}</dd>
                </div>
                <div>
                  <dt>Classes attended</dt>
                  <dd>{attendanceStats?.presentCount || 0}</dd>
                </div>
                <div>
                  <dt>Fees due</dt>
                  <dd>{money(dynamicAmounts.remainingAmount)}</dd>
                </div>
              </dl>
            </section>

            <div className="ov-tiles">
              {[
                { icon: BookOpen, label: 'Course', value: profileData.courseName || '—' },
                {
                  icon: TrendingUp,
                  label: 'Attendance',
                  value: `${attendancePercentage}%`,
                  tone: staffAttTone(attendancePercentage),
                },
                {
                  icon: CheckCircle,
                  label: 'Classes attended',
                  value: attendanceStats?.presentCount || 0,
                },
                { icon: Clock, label: 'Classes held', value: attendanceStats?.totalLectures || 0 },
                { icon: CreditCard, label: 'Fees paid', value: money(dynamicAmounts.paidAmount) },
                { icon: Layers, label: 'Batches', value: batchesData.length },
              ].map(({ icon: Icon, label, value, tone }) => (
                <div className="ov-tile" key={label}>
                  <span className="ov-tile-label">
                    <Icon size={13} aria-hidden="true" />
                    {label}
                  </span>
                  <span className={`ov-tile-value${tone ? ` sp-att ${tone}` : ''}`}>{value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="td-tabs">
            <div className="td-tabs-strip" role="tablist">
              {TABS.map((tab) => (
                <button
                  type="button"
                  key={tab.key}
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  className={`td-tab${activeTab === tab.key ? ' is-on' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                  {tab.count !== undefined && <span className="td-tab-n">{tab.count}</span>}
                </button>
              ))}
            </div>

            <div className="td-tabs-body">
              {activeTab === 'Account Details' && (
                <div className="ov-duo">
                  <section className="ov-panel">
                    <header className="ov-panel-head">
                      <h2 className="ov-panel-title">Personal details</h2>
                    </header>
                    <dl className="sp-fields">
                      {PERSONAL.map(({ label, value }) => (
                        <div key={label}>
                          <dt>{label}</dt>
                          <dd className={dash(value) ? '' : 'is-empty'}>{dash(value) || '—'}</dd>
                        </div>
                      ))}
                      <div>
                        <dt>Permanent address</dt>
                        <dd className={dash(profileData.permanentAddress) ? '' : 'is-empty'}>
                          {dash(profileData.permanentAddress) || '—'}
                        </dd>
                      </div>
                      <div>
                        <dt>Current address</dt>
                        <dd className={dash(profileData.currentAddress) ? '' : 'is-empty'}>
                          {dash(profileData.currentAddress) || '—'}
                        </dd>
                      </div>
                    </dl>
                  </section>

                  <section className="ov-panel">
                    <header className="ov-panel-head">
                      <h2 className="ov-panel-title">Academic and documents</h2>
                    </header>
                    <dl className="sp-fields">
                      {ACADEMIC.map(({ label, value }) => (
                        <div key={label}>
                          <dt>{label}</dt>
                          <dd className={dash(value) ? '' : 'is-empty'}>{dash(value) || '—'}</dd>
                        </div>
                      ))}
                    </dl>
                  </section>
                </div>
              )}

              {activeTab === 'Batch Information' && (
                <section className="td-tbl td-tbl--spbatches">
                  <header className="td-tbl-head">
                    <div>
                      <h5 className="td-tbl-title">Batches</h5>
                      <p className="td-tbl-sub">
                        {batchesData.length} batch{batchesData.length === 1 ? '' : 'es'} on this
                        record
                      </p>
                    </div>
                  </header>

                  {batchLoading ? (
                    <SkeletonGroup label="Loading batches" className="td-tbl-rows">
                      {[0, 1, 2].map((i) => (
                        <div className="td-tbl-row" key={i}>
                          <div className="td-tbl-name lms-skel-stack">
                            <Skeleton width="70" />
                            <Skeleton width="25" />
                          </div>
                          <div className="td-tbl-name"><Skeleton width="55" /></div>
                          <div className="td-tbl-name"><Skeleton width="55" /></div>
                          <div className="td-tbl-name lms-skel-stack">
                            <Skeleton width="40" />
                            <Skeleton width="40" />
                          </div>
                        </div>
                      ))}
                    </SkeletonGroup>
                  ) : batchesData.length === 0 ? (
                    <p className="td-tbl-empty">This student is not in any batch yet.</p>
                  ) : (
                    <>
                      {/* Four labels against four tracks in --spbatches. */}
                      <div className="td-tbl-cols">
                        <span>Batch</span>
                        <span>Course</span>
                        <span>Trainer</span>
                        <span>Runs</span>
                      </div>
                      <div className="td-tbl-rows">
                        {batchesData.map((batch) => (
                          <div className="td-tbl-row" key={batch._id || batch.id}>
                            <div className="td-tbl-name">
                              <b>{batch.name || 'Batch'}</b>
                              <small>{batch.mode || 'Online'}</small>
                            </div>
                            <div className="td-tbl-name">
                              <b>{batch.courseDetails?.name || '—'}</b>
                            </div>
                            <div className="td-tbl-name">
                              <b>{batch.trainerDetails?.trainerName || 'Not assigned'}</b>
                            </div>
                            <div className="td-tbl-name">
                              <b>{fmtDay(batch.startTime)}</b>
                              <small>to {fmtDay(batch.endTime)}</small>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </section>
              )}

              {activeTab === 'Attendance' && (
                <section className="td-tbl td-tbl--spatt">
                  <header className="td-tbl-head">
                    <div>
                      <h5 className="td-tbl-title">Attendance by batch</h5>
                      <p className="td-tbl-sub">
                        {attendancePercentage}% overall ·{' '}
                        {attendanceStats?.presentCount || 0} of{' '}
                        {attendanceStats?.totalLectures || 0} classes
                      </p>
                    </div>
                  </header>

                  {attendanceLoading ? (
                    <SkeletonGroup label="Loading attendance" className="td-tbl-rows">
                      {[0, 1, 2].map((i) => (
                        <div className="td-tbl-row" key={i}>
                          <div className="td-tbl-name"><Skeleton width="70" /></div>
                          <div className="td-tbl-name"><Skeleton width="25" /></div>
                          <div className="td-tbl-name"><Skeleton width="25" /></div>
                          <div className="td-tbl-name"><Skeleton shape="pill" /></div>
                        </div>
                      ))}
                    </SkeletonGroup>
                  ) : attendanceByBatch.length === 0 ? (
                    <p className="td-tbl-empty">No attendance recorded.</p>
                  ) : (
                    <>
                      {/* Four labels against four tracks in --spatt. */}
                      <div className="td-tbl-cols">
                        <span>Batch</span>
                        <span>Present</span>
                        <span>Held</span>
                        <span>Attendance</span>
                      </div>
                      <div className="td-tbl-rows">
                        {attendanceByBatch.map(({ batch, batchId, total, present, pct }) => (
                          <div className="td-tbl-row" key={batchId}>
                            <div className="td-tbl-name">
                              <b>{batch.name || 'Batch'}</b>
                              <small>{batch.courseDetails?.name || '—'}</small>
                            </div>
                            <span className="td-tbl-num">{present}</span>
                            <span className="td-tbl-num">{total}</span>
                            <span className={`td-tbl-num sp-att ${staffAttTone(pct)}`}>{pct}%</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </section>
              )}

              {activeTab === 'Billing Information' && (
                <>
                  <div className="ov-tiles">
                    <div className="ov-tile">
                      <span className="ov-tile-label">
                        <CreditCard size={13} aria-hidden="true" />
                        Total fees
                      </span>
                      <span className="ov-tile-value">{money(dynamicAmounts.totalAmount)}</span>
                    </div>
                    <div className="ov-tile">
                      <span className="ov-tile-label">
                        <CheckCircle size={13} aria-hidden="true" />
                        Paid
                      </span>
                      <span className="ov-tile-value">{money(dynamicAmounts.paidAmount)}</span>
                    </div>
                    <div className="ov-tile">
                      <span className="ov-tile-label">
                        <Clock size={13} aria-hidden="true" />
                        Remaining
                      </span>
                      <span className="ov-tile-value">{money(dynamicAmounts.remainingAmount)}</span>
                    </div>
                  </div>

                  <section className="td-tbl td-tbl--spreceipts">
                    <header className="td-tbl-head">
                      <div>
                        <h5 className="td-tbl-title">Receipts</h5>
                        <p className="td-tbl-sub">
                          {receiptsData.length} receipt{receiptsData.length === 1 ? '' : 's'} ·
                          newest first
                        </p>
                      </div>
                    </header>

                    {receiptsData.length === 0 ? (
                      <p className="td-tbl-empty">No receipts for this student.</p>
                    ) : (
                      <>
                        {/* Four labels against four tracks in --spreceipts. */}
                        <div className="td-tbl-cols">
                          <span>Receipt</span>
                          <span>Date</span>
                          <span>Type</span>
                          <span>Paid</span>
                        </div>
                        <div className="td-tbl-rows">
                          {receiptsData.map((receipt) => (
                            <div
                              className="td-tbl-row td-tbl-row--tap"
                              key={receipt._id || receipt.receiptNo}
                              onClick={() => handleViewReceipt(receipt)}
                            >
                              <div className="td-tbl-name">
                                <b>#{receipt.receiptNo || receipt._id?.slice(-6) || '—'}</b>
                                <small>{receipt.courseIds?.[0]?.name || '—'}</small>
                              </div>
                              <div className="td-tbl-name">
                                <b>{receipt.date || '—'}</b>
                              </div>
                              <span className="td-tbl-pill">{receipt.status || '—'}</span>
                              <span className="td-tbl-num">{money(receipt.amountPaid)}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </section>
                </>
              )}
            </div>
          </div>
        </div>

        {receiptModal}
        {syllabusModal}
      </div>
    );
  }

  /* =============================================================
     STUDENT VIEW

     LinkedIn's profile page: the identity card over its cover, a
     section nav, and the section itself — with the standing figures
     in the rail beside it.
     ============================================================= */
  return (
    <div className="dashboard-wrapper">
      <div className="pf">
        <div className="pf-grid">
          <main className="pf-main">
            <section className="pf-card">
              <div className="pf-id-cover" aria-hidden="true" />
              <div className="pf-id-body">
                <span className="pf-id-avatar">{initialsOf(displayName)}</span>
                <h1 className="pf-id-name">{displayName}</h1>
                {profileData.courseName && (
                  <p className="pf-id-headline">
                    Learning {profileData.courseName} at SevenMentor
                  </p>
                )}

                <p className="pf-id-meta">
                  {displayEmail && (
                    <span>
                      <Mail size={13} aria-hidden="true" />
                      {displayEmail}
                    </span>
                  )}
                  {profileData.phone && (
                    <>
                      <span className="pf-id-dot" aria-hidden="true">·</span>
                      <span>
                        <Phone size={13} aria-hidden="true" />
                        {profileData.phone}
                      </span>
                    </>
                  )}
                  {(profileData.city || profileData.state) && (
                    <>
                      <span className="pf-id-dot" aria-hidden="true">·</span>
                      <span>
                        <MapPin size={13} aria-hidden="true" />
                        {[profileData.city, profileData.state].filter(Boolean).join(', ')}
                      </span>
                    </>
                  )}
                </p>

                {profileData.studentNumber && (
                  <div className="pf-id-num">
                    <span className="pf-id-num-label">Student ID</span>
                    <span className="pf-id-num-value">{profileData.studentNumber}</span>
                  </div>
                )}
              </div>
            </section>

            <div className="pf-card">
              <div className="pf-tabs" role="tablist">
                {TABS.map(({ key, label, icon: Icon }) => (
                  <button
                    type="button"
                    key={key}
                    role="tab"
                    aria-selected={activeTab === key}
                    className={`pf-tab${activeTab === key ? ' is-on' : ''}`}
                    onClick={() => setActiveTab(key)}
                  >
                    <Icon size={15} aria-hidden="true" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {activeTab === 'Account Details' && (
              <>
                <section className="pf-card">
                  <div className="pf-card-head">
                    <h2 className="pf-card-title">
                      <User size={18} aria-hidden="true" />
                      Personal information
                    </h2>
                  </div>
                  <div className="pf-card-body">
                    <dl className="pf-fields">
                      {PERSONAL.map(({ label, value }) => (
                        <div key={label}>
                          <dt>{label}</dt>
                          <dd className={dash(value) ? '' : 'is-empty'}>{dash(value) || '—'}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </section>

                {/* The addresses are the only part of this record a student
                    may change, so they are the only part behind an Edit. */}
                <section className="pf-card">
                  <div className="pf-card-head">
                    <h2 className="pf-card-title">
                      <MapPin size={18} aria-hidden="true" />
                      Address
                    </h2>
                    {!editMode && (
                      <button
                        type="button"
                        className="pf-btn pf-btn--ghost"
                        onClick={() => setEditMode(true)}
                      >
                        <Edit size={14} aria-hidden="true" />
                        Edit
                      </button>
                    )}
                  </div>

                  <div className="pf-card-body">
                    {editMode ? (
                      <Formik
                        initialValues={{
                          permanentAddress: profileData.permanentAddress || '',
                          currentAddress: profileData.currentAddress || '',
                        }}
                        onSubmit={handleSubmit}
                        enableReinitialize
                      >
                        {({ dirty }) => (
                          <FormikForm>
                            <dl className="pf-fields">
                              <div className="pf-field--wide">
                                <dt>Permanent address</dt>
                                <dd>
                                  <Field
                                    as="textarea"
                                    className="pf-input"
                                    name="permanentAddress"
                                    rows={2}
                                  />
                                </dd>
                              </div>
                              <div className="pf-field--wide">
                                <dt>Current address</dt>
                                <dd>
                                  <Field
                                    as="textarea"
                                    className="pf-input"
                                    name="currentAddress"
                                    rows={2}
                                  />
                                </dd>
                              </div>
                            </dl>

                            <div className="pf-id-actions">
                              <button type="submit" className="pf-btn" disabled={!dirty}>
                                <Save size={14} aria-hidden="true" />
                                Save
                              </button>
                              <button
                                type="button"
                                className="pf-btn pf-btn--ghost"
                                onClick={() => setEditMode(false)}
                              >
                                <X size={14} aria-hidden="true" />
                                Cancel
                              </button>
                            </div>
                          </FormikForm>
                        )}
                      </Formik>
                    ) : (
                      <dl className="pf-fields">
                        <div className="pf-field--wide">
                          <dt>Permanent address</dt>
                          <dd className={dash(profileData.permanentAddress) ? '' : 'is-empty'}>
                            {dash(profileData.permanentAddress) || 'Not provided'}
                          </dd>
                        </div>
                        <div className="pf-field--wide">
                          <dt>Current address</dt>
                          <dd className={dash(profileData.currentAddress) ? '' : 'is-empty'}>
                            {dash(profileData.currentAddress) || 'Not provided'}
                          </dd>
                        </div>
                      </dl>
                    )}
                  </div>
                </section>

                <section className="pf-card">
                  <div className="pf-card-head">
                    <h2 className="pf-card-title">
                      <Award size={18} aria-hidden="true" />
                      Education and documents
                    </h2>
                  </div>
                  <div className="pf-card-body">
                    <dl className="pf-fields">
                      {ACADEMIC.map(({ label, value }) => (
                        <div key={label}>
                          <dt>{label}</dt>
                          <dd className={dash(value) ? '' : 'is-empty'}>{dash(value) || '—'}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </section>
              </>
            )}

            {activeTab === 'Batch Information' && (
              <section className="pf-card">
                <div className="pf-card-head">
                  <h2 className="pf-card-title">
                    <Layers size={18} aria-hidden="true" />
                    Batches
                  </h2>
                </div>
                <p className="pf-card-sub">
                  {batchesData.length} batch{batchesData.length === 1 ? '' : 'es'} you are enrolled
                  in
                </p>

                {batchLoading ? (
                  <div className="pf-skel">
                    <div className="pf-skel-bar pf-skel-bar--third" />
                    <div className="pf-skel-bar" />
                  </div>
                ) : batchesData.length === 0 ? (
                  <div className="pf-empty">
                    <span className="pf-empty-mark">
                      <Layers size={22} aria-hidden="true" />
                    </span>
                    <h3 className="pf-empty-title">No batches yet</h3>
                    <p className="pf-empty-text">
                      Once you are enrolled in a batch it will show up here.
                    </p>
                  </div>
                ) : (
                  <ul className="pf-list">
                    {batchesData.map((batch) => (
                      <li key={batch._id || batch.id}>
                        <div className="pf-row">
                          <span className="pf-row-mark">{initialsOf(batch.name || 'B')}</span>
                          <div className="pf-row-main">
                            <div className="pf-row-name">{batch.name || 'Batch'}</div>
                            <div className="pf-row-sub">
                              {batch.courseDetails?.name || 'No course specified'}
                            </div>
                            <div className="pf-row-facts">
                              <span>
                                <User size={12} aria-hidden="true" />
                                {batch.trainerDetails?.trainerName || 'Not assigned'}
                              </span>
                              <span aria-hidden="true">·</span>
                              <span>
                                <Calendar size={12} aria-hidden="true" />
                                {fmtDay(batch.startTime)} – {fmtDay(batch.endTime)}
                              </span>
                            </div>
                          </div>
                          <span className="pf-chip">{batch.mode || 'Online'}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            {activeTab === 'Attendance' && (
              <section className="pf-card">
                <div className="pf-card-head">
                  <h2 className="pf-card-title">
                    <CheckCircle size={18} aria-hidden="true" />
                    Attendance
                  </h2>
                  <span className={`pf-chip${attendancePercentage >= 80 ? ' pf-chip--good' : attendancePercentage < 60 ? ' pf-chip--bad' : ' pf-chip--warn'}`}>
                    {attendancePercentage}% overall
                  </span>
                </div>
                <p className="pf-card-sub">
                  {attendanceStats?.presentCount || 0} of {attendanceStats?.totalLectures || 0}{' '}
                  classes attended
                </p>

                {attendanceLoading ? (
                  <div className="pf-skel">
                    <div className="pf-skel-bar pf-skel-bar--third" />
                    <div className="pf-skel-bar" />
                  </div>
                ) : attendanceByBatch.length === 0 ? (
                  <div className="pf-empty">
                    <span className="pf-empty-mark">
                      <CheckCircle size={22} aria-hidden="true" />
                    </span>
                    <h3 className="pf-empty-title">Nothing recorded yet</h3>
                    <p className="pf-empty-text">
                      Attendance appears here once your trainer starts marking classes.
                    </p>
                  </div>
                ) : (
                  <ul className="pf-list">
                    {attendanceByBatch.map(({ batch, batchId, total, present, pct }) => (
                      <li key={batchId}>
                        <div className="pf-row">
                          <div className="pf-row-main">
                            <div className="pf-row-name">{batch.name || 'Batch'}</div>
                            <div className="pf-row-sub">
                              {batch.courseDetails?.name || 'No course specified'}
                            </div>
                            <div className="pf-meter">
                              <div className="pf-meter-top">
                                <span>
                                  {present} of {total} classes
                                </span>
                                <span className="pf-meter-num">{pct}%</span>
                              </div>
                              <div className="pf-meter-track">
                                <span
                                  className={`pf-meter-fill ${attTone(pct)}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            {activeTab === 'Billing Information' && (
              <>
                <section className="pf-card">
                  <div className="pf-card-head">
                    <h2 className="pf-card-title">
                      <CreditCard size={18} aria-hidden="true" />
                      Fees
                    </h2>
                  </div>
                  <div className="pf-card-body">
                    <div className="pf-money">
                      <div className="pf-money-tile">
                        <span className="pf-money-label">
                          <CreditCard size={12} aria-hidden="true" />
                          Total
                        </span>
                        <span className="pf-money-value">{money(dynamicAmounts.totalAmount)}</span>
                      </div>
                      <div className="pf-money-tile">
                        <span className="pf-money-label">
                          <CheckCircle size={12} aria-hidden="true" />
                          Paid
                        </span>
                        <span className="pf-money-value is-paid">
                          {money(dynamicAmounts.paidAmount)}
                        </span>
                      </div>
                      <div className="pf-money-tile">
                        <span className="pf-money-label">
                          <Clock size={12} aria-hidden="true" />
                          Remaining
                        </span>
                        <span
                          className={`pf-money-value${
                            Number(dynamicAmounts.remainingAmount) > 0 ? ' is-due' : ''
                          }`}
                        >
                          {money(dynamicAmounts.remainingAmount)}
                        </span>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="pf-card">
                  <div className="pf-card-head">
                    <h2 className="pf-card-title">
                      <FileText size={18} aria-hidden="true" />
                      Receipts
                    </h2>
                  </div>
                  <p className="pf-card-sub">
                    {receiptsData.length} receipt{receiptsData.length === 1 ? '' : 's'}
                  </p>

                  {receiptsData.length === 0 ? (
                    <div className="pf-empty">
                      <span className="pf-empty-mark">
                        <FileText size={22} aria-hidden="true" />
                      </span>
                      <h3 className="pf-empty-title">No receipts yet</h3>
                      <p className="pf-empty-text">
                        Receipts appear here once a payment is recorded against your account.
                      </p>
                    </div>
                  ) : (
                    <ul className="pf-list">
                      {receiptsData.map((receipt) => (
                        <li key={receipt._id || receipt.receiptNo}>
                          <div className="pf-row">
                            <span className="pf-row-mark">
                              <FileText size={18} aria-hidden="true" />
                            </span>
                            <div className="pf-row-main">
                              <div className="pf-row-name">
                                Receipt #{receipt.receiptNo || receipt._id?.slice(-6) || '—'}
                              </div>
                              <div className="pf-row-sub">
                                {receipt.courseIds?.[0]?.name || 'Course'}
                              </div>
                              <div className="pf-row-facts">
                                <span>
                                  <Calendar size={12} aria-hidden="true" />
                                  {receipt.date || '—'}
                                </span>
                                <span aria-hidden="true">·</span>
                                <span>{receipt.status || '—'}</span>
                              </div>
                              <div className="pf-id-actions">
                                <button
                                  type="button"
                                  className="pf-btn pf-btn--ghost"
                                  onClick={() => handleViewReceipt(receipt)}
                                >
                                  <Eye size={14} aria-hidden="true" />
                                  View
                                </button>
                              </div>
                            </div>
                            <span className="pf-row-side">{money(receipt.amountPaid)}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </>
            )}
          </main>

          <aside className="pf-rail">
            <section className="pf-card">
              <div className="pf-card-head">
                <h2 className="pf-card-title">Attendance</h2>
              </div>
              <div className="pf-ring" role="img" aria-label={`Attendance ${attendancePercentage} percent`}>
                <svg viewBox="0 0 120 120" aria-hidden="true">
                  <circle className="pf-ring-track" cx="60" cy="60" r="52" fill="none" strokeWidth="10" />
                  <circle
                    cx="60"
                    cy="60"
                    r="52"
                    fill="none"
                    strokeWidth="10"
                    strokeLinecap="round"
                    stroke={
                      attendancePercentage >= 80
                        ? '#01754F'
                        : attendancePercentage >= 60
                        ? '#8A5407'
                        : '#B24020'
                    }
                    strokeDasharray={RING_LEN}
                    strokeDashoffset={RING_LEN * (1 - attendancePercentage / 100)}
                  />
                </svg>
                <div className="pf-ring-face">
                  <b>{attendancePercentage}%</b>
                  <small>attended</small>
                </div>
              </div>
              <div className="pf-stat">
                <span className="pf-stat-label">Classes attended</span>
                <span className="pf-stat-value">{attendanceStats?.presentCount || 0}</span>
              </div>
              <div className="pf-stat">
                <span className="pf-stat-label">Classes held</span>
                <span className="pf-stat-value">{attendanceStats?.totalLectures || 0}</span>
              </div>
              <div className="pf-stat">
                <span className="pf-stat-label">Batches</span>
                <span className="pf-stat-value">{batchesData.length}</span>
              </div>
            </section>

            <section className="pf-card">
              <div className="pf-card-head">
                <h2 className="pf-card-title">Fees</h2>
              </div>
              <div className="pf-stat">
                <span className="pf-stat-label">Total</span>
                <span className="pf-stat-value">{money(dynamicAmounts.totalAmount)}</span>
              </div>
              <div className="pf-stat">
                <span className="pf-stat-label">Paid</span>
                <span className="pf-stat-value">{money(dynamicAmounts.paidAmount)}</span>
              </div>
              <div className="pf-stat">
                <span className="pf-stat-label">Remaining</span>
                <span className="pf-stat-value">{money(dynamicAmounts.remainingAmount)}</span>
              </div>
            </section>

            {motivationalQuote && (
              <section className="pf-card">
                <p className="pf-quote">“{motivationalQuote}”</p>
              </section>
            )}
          </aside>
        </div>
      </div>

      {receiptModal}
      {syllabusModal}
    </div>
  );
};

export default StudentProfile;
